import { WORKSPACE_VIEWS } from './features/workspaceState';
import {
  EVENT_TYPES,
  NAVIGATION_PRESETS,
  NAVIGATION_PRESET_BY_KEY,
} from './features/registry';

export const DEEP_LINK_ROUTES = {
  TODAY: 'today',
  QUICK_ADD: 'quick-add',
  ATLAS: 'atlas',
  DIA: 'dia',
  HISTORIAS: 'historias',
  DADOS: 'dados',
};

const ROUTE_ALIASES = {
  hoje: DEEP_LINK_ROUTES.TODAY,
  today: DEEP_LINK_ROUTES.TODAY,
  'quick-add': DEEP_LINK_ROUTES.QUICK_ADD,
  quickadd: DEEP_LINK_ROUTES.QUICK_ADD,
  'entrada-rapida': DEEP_LINK_ROUTES.QUICK_ADD,
  atlas: DEEP_LINK_ROUTES.ATLAS,
  dia: DEEP_LINK_ROUTES.DIA,
  historias: DEEP_LINK_ROUTES.HISTORIAS,
  stories: DEEP_LINK_ROUTES.HISTORIAS,
  dados: DEEP_LINK_ROUTES.DADOS,
  data: DEEP_LINK_ROUTES.DADOS,
};

/** Query keys written by serialize (aliases accepted on parse). */
export const WORKSPACE_URL_PARAMS = [
  'view',
  'start',
  'end',
  'date',
  'story',
  'types',
  'lens',
  'isolatedLayer',
];

/** Alias params stripped/accepted so legacy + plan 3.7 names both work. */
const WORKSPACE_URL_ALIASES = ['workspace', 'startDate', 'endDate'];

export const DEFAULT_WORKSPACE_URL_STATE = {
  view: 'atlas',
  start: null,
  end: null,
  date: null,
  story: null,
  types: null,
  lens: null,
  isolatedLayer: null,
};

export const URL_STATE_REPLACE_DEBOUNCE_MS = 200;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const VALID_TYPE_KEYS = new Set(EVENT_TYPES.map((et) => et.key));
const VALID_LENS_KEYS = new Set(NAVIGATION_PRESETS.map((p) => p.key));

const ALL_TYPE_KEYS = EVENT_TYPES.map((et) => et.key);

const ROUTE_TO_VIEW = {
  [DEEP_LINK_ROUTES.ATLAS]: 'atlas',
  [DEEP_LINK_ROUTES.DIA]: 'dia',
  [DEEP_LINK_ROUTES.HISTORIAS]: 'historias',
  [DEEP_LINK_ROUTES.DADOS]: 'dados',
};

const isValidDate = (value) => typeof value === 'string' && DATE_RE.test(value);

const toSearchParams = (search) => {
  if (search instanceof URLSearchParams) return search;
  const raw = typeof search === 'string' ? search : '';
  const normalized = raw.startsWith('?') ? raw.slice(1) : raw;
  return new URLSearchParams(normalized);
};

const parseTypesParam = (raw) => {
  if (raw == null || raw === '') return null;
  const parts = String(raw)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const valid = [...new Set(parts.filter((key) => VALID_TYPE_KEYS.has(key)))];
  return valid.length > 0 ? valid : null;
};

const typesEqualAll = (types) => {
  if (!Array.isArray(types) || types.length !== ALL_TYPE_KEYS.length) return false;
  const set = new Set(types);
  return ALL_TYPE_KEYS.every((key) => set.has(key));
};

const sameTypeSet = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((key) => set.has(key));
};

/**
 * Lê estado serializável do workspace a partir de query string (ou URLSearchParams).
 * Valores inválidos são ignorados; retorna defaults do plano Épico 31 / 3.7.
 *
 * Params canônicos: view, start, end, date, story, types, lens, isolatedLayer.
 * Aliases de parse: workspace→view, startDate→start, endDate→end.
 */
export const parseWorkspaceUrl = (search = '') => {
  const params = toSearchParams(search);
  const next = { ...DEFAULT_WORKSPACE_URL_STATE };

  const view = params.get('view') || params.get('workspace');
  if (view && WORKSPACE_VIEWS.includes(view)) {
    next.view = view;
  }

  const start = params.get('start') || params.get('startDate');
  if (isValidDate(start)) next.start = start;

  const end = params.get('end') || params.get('endDate');
  if (isValidDate(end)) next.end = end;

  const date = params.get('date');
  if (isValidDate(date)) next.date = date;

  const story = params.get('story');
  if (story && /^[\w-]+$/.test(story)) next.story = story;

  const types = parseTypesParam(params.get('types'));
  if (types) next.types = types;

  const lens = params.get('lens');
  if (lens && VALID_LENS_KEYS.has(lens)) {
    next.lens = lens;
  }

  // lens sem types: expande filterKeys do preset (exceto "all" = default null)
  if (!next.types && next.lens && next.lens !== 'all') {
    const preset = NAVIGATION_PRESET_BY_KEY[next.lens];
    if (preset?.filterKeys?.length) {
      next.types = preset.filterKeys.filter((key) => VALID_TYPE_KEYS.has(key));
    }
  }

  const isolatedLayer = params.get('isolatedLayer');
  if (isolatedLayer && VALID_TYPE_KEYS.has(isolatedLayer)) {
    next.isolatedLayer = isolatedLayer;
  }

  return next;
};

/**
 * Serializa estado do workspace em query string. Omite defaults (`view=atlas`,
 * types=todos, lens=all/null, isolatedLayer vazio).
 * Preserva outros params (ex.: `atlasVivo=0`) em `existingSearch`.
 */
export const serializeWorkspaceUrl = (workspace = {}, existingSearch = '') => {
  const params = toSearchParams(existingSearch);

  WORKSPACE_URL_PARAMS.forEach((key) => params.delete(key));
  WORKSPACE_URL_ALIASES.forEach((key) => params.delete(key));

  const view = workspace.view ?? DEFAULT_WORKSPACE_URL_STATE.view;
  if (view && view !== DEFAULT_WORKSPACE_URL_STATE.view && WORKSPACE_VIEWS.includes(view)) {
    params.set('view', view);
  }

  if (isValidDate(workspace.start)) params.set('start', workspace.start);
  if (isValidDate(workspace.end)) params.set('end', workspace.end);
  if (isValidDate(workspace.date)) params.set('date', workspace.date);

  const story = workspace.story;
  if (story && /^[\w-]+$/.test(story)) {
    params.set('story', story);
  }

  const types = Array.isArray(workspace.types)
    ? [...new Set(workspace.types.filter((k) => VALID_TYPE_KEYS.has(k)))]
    : null;
  if (types && types.length > 0 && !typesEqualAll(types)) {
    params.set('types', types.join(','));
  }

  const lens = workspace.lens;
  if (lens && VALID_LENS_KEYS.has(lens) && lens !== 'all') {
    const preset = NAVIGATION_PRESET_BY_KEY[lens];
    const effectiveTypes = types && types.length ? types : ALL_TYPE_KEYS;
    if (preset && sameTypeSet(effectiveTypes, preset.filterKeys)) {
      params.set('lens', lens);
    }
  }

  const isolatedLayer = workspace.isolatedLayer;
  if (isolatedLayer && VALID_TYPE_KEYS.has(isolatedLayer)) {
    params.set('isolatedLayer', isolatedLayer);
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

/**
 * Interpreta uma URL de deep link (custom scheme do Capacitor, ex.:
 * `com.nedisonn.visitedplaces://hoje`, ou universal link
 * `https://.../quick-add`) e retorna a rota interna correspondente, ou
 * `null` se não reconhecida. Puro — não depende do plugin nativo, só de
 * `URL`, para ser testável fora do Capacitor.
 */
export const parseDeepLinkUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  let path;
  try {
    const parsed = new URL(url);
    path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : parsed.hostname;
  } catch {
    return null;
  }

  const segment = String(path || '').replace(/^\/+|\/+$/g, '').toLowerCase();
  return ROUTE_ALIASES[segment] || null;
};

/**
 * Combina segmento de rota Capacitor/universal link com query params do workspace.
 * Ex.: `app://historias?story=insights` → `{ route, workspace: { view: 'historias', story: 'insights' } }`.
 */
export const parseDeepLinkPayload = (url) => {
  const route = parseDeepLinkUrl(url);
  let search = '';
  try {
    search = new URL(url).search;
  } catch {
    // ignore
  }

  const workspace = parseWorkspaceUrl(search);

  if (route && ROUTE_TO_VIEW[route]) {
    workspace.view = ROUTE_TO_VIEW[route];
  } else if (route === DEEP_LINK_ROUTES.TODAY) {
    workspace.view = 'dia';
  } else if (route === DEEP_LINK_ROUTES.QUICK_ADD) {
    workspace.view = 'dados';
    workspace.story = workspace.story || 'import-health';
  }

  return { route, workspace };
};
